import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/data';
import { supabase } from '../lib/supabase';
import { Chead, ErrorBox, useToast } from '../ui/kit';

type Section = { id: string; slug: string; title: string; summary: string | null; body_md: string };

// Placeholder text written by the first import; it is shown as an empty section.
const isPlaceholder = (body: string) => /^_?Imported from Lead Engine 2\.0/i.test(body.trim());

export function Playbook() {
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const q = useQuery({
    queryKey: ['playbook'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('playbook_sections')
        .select('id, slug, title, summary, body_md')
        .order('sort');
      if (error) throw new Error(error.message);
      return data as Section[];
    },
  });

  async function save(id: string) {
    const { error } = await supabase.rpc('save_playbook_section', { p_id: id, p_body: draft });
    if (error) return toast(error.message);
    await queryClient.invalidateQueries({ queryKey: ['playbook'] });
    setEditing(null);
    toast('Saved');
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Playbook</span>
          </h1>
          <p>How we sell: stages, scripts, ladders. Visible only to owners, who can edit it.</p>
        </div>
      </div>
      <ErrorBox error={q.error} />
      {q.isLoading && <div className="card loading">Loading…</div>}
      {(q.data ?? []).map((s) => (
        <section key={s.id} className="card sec">
          <Chead label={s.title} cls="g" />
          {s.summary && <p className="sub">{s.summary}</p>}
          {editing === s.id ? (
            <div className="pb-edit" style={{ padding: '0 26px 22px' }}>
              <textarea
                aria-label={`Text of ${s.title}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn sm primary" onClick={() => void save(s.id)}>
                  Save
                </button>
                <button className="btn sm ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: '0 26px 22px' }}>
              {isPlaceholder(s.body_md) || !s.body_md.trim() ? (
                <p className="m" style={{ margin: 0 }}>
                  Nothing written here yet.
                </p>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{s.body_md}</div>
              )}
              <button
                className="btn sm"
                style={{ marginTop: 12 }}
                onClick={() => {
                  setDraft(isPlaceholder(s.body_md) ? '' : s.body_md);
                  setEditing(s.id);
                }}
              >
                Edit
              </button>
            </div>
          )}
        </section>
      ))}
      {q.data && !q.data.length && <div className="card empty">No sections yet.</div>}
    </>
  );
}
